import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const DebtDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDebts, setSelectedDebts] = useState(['Credit cards', 'Personal Loan', 'Mortgage']);
  const [paymentStatus, setPaymentStatus] = useState('Yes');

  const debtTypes = [
    'Credit cards',
    'Student loan',
    'Car Loan',
    'Buy-Now-Pay-Later',
    'Personal Loan',
    'Family loan',
    'Mortgage',
    'Overdue Tax',
    'Pawn shop loan',
    'Work & Income Debt'
  ];

  const toggleDebt = (debt: string) => {
    setSelectedDebts(prev =>
      prev.includes(debt)
        ? prev.filter(d => d !== debt)
        : [...prev, debt]
    );
  };

  const handleContinue = () => {
    navigate('/screen3');
  };

  return (
    <motion.div
      className="bg-[#f0effa] relative h-[100dvh] w-full overflow-y-auto overscroll-contain"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.div
        className="absolute top-[60px] left-0 right-0 text-center"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <h1
          className="text-[40px] font-medium text-[#221a51] leading-tight"
          style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}
        >
          Debt Details
        </h1>
      </motion.div>

      {/* Subheading */}
      <motion.p
        className="absolute top-[140px] left-0 right-0 text-center px-[20px] text-[16px] font-medium text-[#6c6881]"
        style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        What kind of debts do you have?
      </motion.p>

      {/* First Card - Debt Types */}
      <motion.div
        className="absolute top-[193px] left-[20px] w-[347.681px] h-[254px] bg-white rounded-[38.631px] border-[0.966px] border-black shadow-[5.795px_5.795px_0px_0px_#221a51] flex items-center justify-center"
        initial={{ scale: 0.9, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5, type: 'spring', stiffness: 120, damping: 12 }}
        whileHover={{ rotate: -0.5, y: -2 }}
        whileTap={{ scale: 0.995 }}
      >
        <div className="flex flex-wrap gap-[7px] px-[10px] w-[330px] justify-start">
          {debtTypes.map((debt, index) => (
            <motion.button
              key={debt}
              onClick={() => toggleDebt(debt)}
              className={`
                flex items-center justify-center gap-[4px] px-[9px] py-[6px] rounded-[38.508px] transition-all text-center
                ${selectedDebts.includes(debt)
                  ? 'bg-[#9b86ea] text-white shadow-[3.081px_3.081px_0px_0px_#221a51] border-[2.311px] border-[#221a51] hover:bg-[#8b76da]'
                  : 'bg-white text-[#737999] border-[0.77px] border-[#ebe8ff] hover:bg-gray-50'
                }
              `}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 + index * 0.05 }}
            >
              <span className="text-[11.5px] font-bold leading-[150%] whitespace-nowrap" style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}>
                {debt}
              </span>
              {selectedDebts.includes(debt) && (
                <div className="bg-[#221a51] rounded-full w-[14px] h-[14px] flex items-center justify-center flex-shrink-0">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Second Card - Payment Status */}
      <motion.div
        className="absolute top-[472px] left-[20px] w-[347.681px] h-[254px] bg-white rounded-[38.631px] border-[0.966px] border-black shadow-[5.795px_5.795px_0px_0px_#221a51]"
        initial={{ scale: 0.9, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5, type: 'spring', stiffness: 120, damping: 12 }}
        whileHover={{ rotate: 0.5, y: -2 }}
        whileTap={{ scale: 0.995 }}
      >
        <div className="w-full px-[30px] pt-[35px]">
          <p className="text-[14px] font-bold text-[#221a51] mb-[20px] text-center leading-[140%]"
            style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}>
            Are you behind on your debt payments?
          </p>
          <div className="flex justify-start gap-[40px]">
            <button
              onClick={() => setPaymentStatus('Yes')}
              className="flex items-center gap-[10px] hover:opacity-80 transition-all"
            >
              <div className={`
                w-[20px] h-[20px] rounded-full border-[1.5px] flex items-center justify-center transition-all
                ${paymentStatus === 'Yes' ? 'border-[#221a51]' : 'border-gray-400'}
              `}>
                {paymentStatus === 'Yes' && (
                  <div className="w-[12px] h-[12px] rounded-full bg-[#9b86ea]" />
                )}
              </div>
              <span className={`text-[16px] ${paymentStatus === 'Yes' ? 'text-[#221a51] font-medium' : 'text-gray-600'}`}
                style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}>
                Yes
              </span>
            </button>
            <button
              onClick={() => setPaymentStatus('No')}
              className="flex items-center gap-[10px] hover:opacity-80 transition-all"
            >
              <div className={`
                w-[20px] h-[20px] rounded-full border-[1.5px] flex items-center justify-center transition-all
                ${paymentStatus === 'No' ? 'border-[#221a51]' : 'border-gray-400'}
              `}>
                {paymentStatus === 'No' && (
                  <div className="w-[12px] h-[12px] rounded-full bg-[#9b86ea]" />
                )}
              </div>
              <span className={`text-[16px] ${paymentStatus === 'No' ? 'text-[#221a51] font-medium' : 'text-gray-600'}`}
                style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}>
                No
              </span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* You chose indicator - positioned below radio buttons, right-aligned */}
      {paymentStatus && (
        <motion.div
          className="absolute top-[620px] right-[40px] w-[211px] bg-[#9b86ea] rounded-[12px] px-[12px] py-[10px] pb-[12px] border-[2.4px] border-[#221a51] shadow-[3.4px_3.4px_0px_0px_#221a51]"
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1, 1.03, 1] }}
          transition={{ delay: 0.2 }}
        >
          <p className="text-[12px] text-white"
            style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}>
            You chose: <span className="font-bold">{paymentStatus}</span>
          </p>
        </motion.div>
      )}

      {/* See my options button */}
      <motion.button
        onClick={handleContinue}
        className="absolute top-[756px] left-[74px] w-[242px] bg-[#9b86ea] text-white p-[12px] rounded-[50px] shadow-[6px_8px_0px_0px_#221a51] font-semibold text-[20px] hover:bg-[#8b76da] hover:shadow-[4px_6px_0px_0px_#221a51] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
        style={{ fontFamily: 'Bricolage Grotesque, system-ui, sans-serif' }}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        whileTap={{ scale: 0.95 }}
      >
        See my options
      </motion.button>
    </motion.div>
  );
};

export default DebtDetailsScreen;
